import { useEffect } from 'react';
import type { Account } from '@adtrackify/at-tracking-event-types';

import { runWhenIdle } from '@/lib/scheduling/run-when-idle';

import { analytics } from '@/lib/analytics/analytics';
import type { AccountAnalyticsContext } from '@/lib/analytics/types/analytics-types';

function calculateTrialDaysRemaining(trialEndsOn?: string): number | undefined {
  if (!trialEndsOn) return undefined;
  const endDate = new Date(trialEndsOn);
  const now = new Date();
  const diffMs = endDate.getTime() - now.getTime();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function buildAnalyticsContext(account: Account): AccountAnalyticsContext {
  const subscription = account.subscription;
  const plan = subscription?.subscriptionPlan;
  const usage = subscription?.planUsage;

  return {
    // Account
    accountId: account.id,
    accountName: account.accountName,
    accountCreatedAt: account.createdAt,
    accountStatus: account.accountStatus,
    accountSubscriptionType: account.accountSubscriptionType,
    onboardingBusinessType: account.onboardingBusinessType,
    onboardingPlatform: account.onboardingPlatform,

    // Subscription
    subscriptionId: subscription?.id,
    subscriptionStatus: subscription?.status,
    paymentStatus: subscription?.paymentStatus,
    paymentGateway: subscription?.paymentGateway,
    currency: subscription?.currency,

    // Plan
    planId: plan?.id,
    planName: plan?.displayName,
    billingInterval: plan?.billingFrequency,
    planPrice: plan?.price,
    planBillingGateway: plan?.billingGateway,
    hasUsageBilling: plan?.enableUsage,

    // Trial
    trialDays: subscription?.trialDays,
    trialStartDate: subscription?.trialStartedOn,
    trialEndDate: subscription?.trialEndsOn,
    trialDaysRemaining: calculateTrialDaysRemaining(subscription?.trialEndsOn),
    isCardlessTrial: subscription?.isCardlessTrial,

    // Usage
    currentMonthUsage: usage?.currentMonthUsage,
    usageExceeded: usage?.usageExceeded,
  };
}

export function useAnalyticsAccountSync(currentAccount: Account | null): void {
  useEffect(() => {
    const accountSnapshot = currentAccount;

    const idleTask = runWhenIdle(() => {
      if (!accountSnapshot) {
        analytics.setAccountContext(null);
        return;
      }

      analytics.setAccountContext(buildAnalyticsContext(accountSnapshot));
    });

    return () => {
      idleTask.cancel();
    };
  }, [currentAccount]);
}
